import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
// import * as Realm from "realm-web";
import { AuthContext } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import getExchangeTeam from '../../MongoDb/GetExchangeTeam';
import { url as API_BASE_URL } from '../../MongoDb/MongoConstants';
import './TeamDetail.css';

// const app = new Realm.App({ id: "thebrainfund-qyrogsg" });
// const API_BASE_URL = 'http://localhost:5000';

function TeamDetail() {
    const { team_symbol } = useParams();
    const { isDarkMode } = useTheme();
    const [teamName, setTeamName] = useState("");
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useContext(AuthContext);
    const [profile, setProfile] = useState(null);
    const [orderType, setOrderType] = useState("buy");
    const [price, setPrice] = useState("");
    const [quantity, setQuantity] = useState("");
    const [orderMessage, setOrderMessage] = useState("");
    const [recentTrades, setRecentTrades] = useState([]);
    const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
    const [todayPrice, setTodayPrice] = useState(null);
    const basePrice = todayPrice?.price ?? 0;
    const minPrice = (basePrice * 0.9).toFixed(2);
    const maxPrice = (basePrice * 1.1).toFixed(2);
    const [highlight, setHighlight] = useState(false);
    const prevPriceRef = React.useRef();
    const [stats24hr, setStats24hr] = useState({ high: null, low: null, volume: null });
    const [teamStats, setTeamStats] = useState({ super_bowl_odds: null, curr_super_bowl_odds: null, projected_wins: null });
    const [priceStats, setPriceStats] = useState({ class_a_price: null, class_b_price: null, next_bet: null });
    const [teamSport, setTeamSport] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Determine championship name based on sport
    const getChampionshipName = (sport) => {
        switch (sport) {
            case 'NFL':
                return 'Super Bowl';
            case 'MLB':
                return 'World Series';
            case 'NHL':
                return 'Stanley Cup';
            case 'NBA':
                return 'NBA Championship';
            case 'Soccer':
                return 'FIFA WC';
            default:
                return 'Championship';
        }
    };

    const sport_champ = getChampionshipName(teamSport);

    const formatDateLabel = (dateString) => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear() % 100;
        return `${month}.${day}.${year}`;
    };

    // 🔁 Fetchers
    const fetchTrades = async () => {
        if (user) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/exchange/trades/symbol/${encodeURIComponent(team_symbol)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                });
                const result = await response.json();
                if (result.success) {
                    setRecentTrades(result.data);
                }
            } catch (error) {
                console.error('Error fetching trades:', error);
            }
        }
    };

    const fetchUserProfile = async () => {
        if (user) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/user/profile/${user.id}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                });
                const result = await response.json();
                if (result.success) {
                    setProfile(result.data);
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
            }
        }
    };

    useEffect(() => {
    const fetchBook = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/exchange/order-book/symbol/${encodeURIComponent(team_symbol)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            const result = await response.json();
            if (result.success) {
                setOrderBook(result.data);
            }
        } catch (error) {
            console.error('Error fetching order book:', error);
        }
    };
    fetchBook();
    }, [team_symbol]);

    // 🔁 Initial data
    useEffect(() => { fetchTrades(); }, [team_symbol, user]);
    useEffect(() => { fetchUserProfile(); }, [user]);

    useEffect(() => {
        // const fetchLatestPrice = () => {
        //     fetch(`http://beatthebook.app/api/data/exchange/symbol/${encodeURIComponent(team_symbol)}`)
        //     .then(res => res.json())
        //     .then(data => {
        //         // setHistory(data.price_history || []);
        //         // setLoading(false);
        //         if (data.current_price) {
        //             setTodayPrice({ price: data.current_price, date: new Date().toISOString().split('T')[0] });
        //         }
        //         if (data.team) {
        //             setTeamName(data.team);
        //         } 
        //     });
        // };
        // fetchLatestPrice();
        const fetchLatestPrice = async () => {
            try {
                const exchangeteam = await getExchangeTeam(team_symbol);
                if (exchangeteam.current_price) {
                    setTodayPrice({ price: exchangeteam.current_price, date: new Date().toISOString().split('T')[0] });
                }
                if (exchangeteam.team) {
                    setTeamName(exchangeteam.team);
                }
                if (exchangeteam.sport) {
                    setTeamSport(exchangeteam.sport);
                }
                // Set team stats
                try {
                    setTeamStats({
                        super_bowl_odds: exchangeteam?.super_bowl_odds || null,
                        current_odds: exchangeteam.current_odds || null,
                        odds_provider: exchangeteam.odds_provider || null,
                        high_odds: exchangeteam.high_odds || null,
                        high_provider: exchangeteam.high_provider || null,
                    });
                } catch (error) {
                    console.error('Error setting team stats:', error);
                    setTeamStats({
                        super_bowl_odds: null,
                        current_odds: null,
                        odds_provider: null,
                        high_odds: null,
                        high_provider: null,
                    });
                }
                // Set price stats
                setPriceStats({
                    class_a_price: exchangeteam.class_a_price,
                    class_b_price: exchangeteam.class_b_price,
                    next_bet: exchangeteam.next_bet,
                });
            }catch (error) {
                console.error('Failed to get exchange data:', error);
            }
        };
        fetchLatestPrice();
        const interval = setInterval(fetchLatestPrice, 1000);
        return () => clearInterval(interval);
    }, [team_symbol]);

    useEffect(() => {
        // fetch(`http://beatthebook.app/api/data/exchange/symbol/${encodeURIComponent(team_symbol)}`)
        //     .then(res => res.json())
        //     .then(data => {
        //         if (data.price_history) {
        //             // Only update if the last date or length is different
        //             const lastCurrent = history[history.length - 1];
        //             const lastNew = data.price_history[data.price_history.length - 1];
        //             if (
        //                 history.length !== data.price_history.length ||
        //                 !lastCurrent ||
        //                 !lastNew ||
        //                 lastCurrent.date !== lastNew.date ||
        //                 lastCurrent.price !== lastNew.price
        //             ) {
        //                 setHistory(data.price_history);
        //             }
        //         }
        //         setLoading(false);
        //     });

        const fetchLatestPriceHistory = async () => {
            try {
                const exchangeteam = await getExchangeTeam(team_symbol);
                if (exchangeteam.price_history) {
                    // Only update if the last date or length is different
                    const lastCurrent = history[history.length - 1];
                    const lastNew = exchangeteam.price_history[exchangeteam.price_history.length - 1];
                    if (
                        history.length !== exchangeteam.price_history.length ||
                        !lastCurrent ||
                        !lastNew ||
                        lastCurrent.date !== lastNew.date ||
                        lastCurrent.price !== lastNew.price
                    ) {
                        // Add FMV calculation to each history entry
                        const historyWithFMV = exchangeteam.price_history.map(entry => {
                            let fmv = null;
                            if (entry.super_bowl_odds !== null && entry.super_bowl_odds !== undefined) {
                                const odds = entry.super_bowl_odds;
                                let impliedProb;
                                
                                // Calculate implied probability from American odds
                                if (odds > 0) {
                                    impliedProb = 100 / (odds + 100);
                                } else {
                                    impliedProb = (-odds) / (-odds + 100);
                                }
                                
                                // Amount needed to bet to make $1000
                                const betAmount = 1000 * (impliedProb / 100);
                                fmv = (betAmount * 100).toFixed(2);
                            }
                            
                            return {
                                ...entry,
                                fmv: fmv
                            };
                        });
                        
                        setHistory(historyWithFMV);
                    }
                }
                setLoading(false);
            }catch (error) {
                console.error('Failed to get exchange data:', error);
            }
        };
        fetchLatestPriceHistory();  
    }, [team_symbol]);

    useEffect(() => {
        if (!todayPrice) {
            return;
        }

        if (prevPriceRef.current === undefined) {
            prevPriceRef.current = todayPrice.price;
            return;
        }

        if (todayPrice.price !== prevPriceRef.current) {
            console.log(
                "Highlight effect: Price changed from",
                prevPriceRef.current,
                "to",
                todayPrice.price,
                "- triggering highlight."
            );
            setHighlight(true);
            const timeout = setTimeout(() => {
                setHighlight(false);
            }, 1000);
            prevPriceRef.current = todayPrice.price;
            return () => {
            };
        }

    }, [todayPrice]);

    useEffect(() => {
        if (todayPrice && (price === "" || price === null)) {
            setPrice(todayPrice.price);
        }
    }, [todayPrice]);

    useEffect(() => {
        async function fetch24HrStats() {
            try {
                const response = await fetch(`${API_BASE_URL}/api/exchange/stats/24h/${encodeURIComponent(team_symbol)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                });
                const result = await response.json();
                if (result.success) {
                    setStats24hr({
                        high: result.data.high,
                        low: result.data.low,
                        volume: result.data.volume
                    });
                }
            } catch (e) {
                console.error('Error fetching 24h stats:', e);
            }
        }
        fetch24HrStats();
    }, [team_symbol]);

    // 🧠 Arrow icon helper
    const getArrow = (curr, prev) => {
        if (prev === undefined) return null;
        if (curr > prev) return <span style={{ color: 'green', fontWeight: 'bold' }}>&uarr;</span>;
        if (curr < prev) return <span style={{ color: 'red', fontWeight: 'bold' }}>&darr;</span>;
        return <span style={{ color: '#888' }}>–</span>;
    };

    const reversedHistory = [...history].reverse();
    const historyMap = Object.fromEntries(history.map(h => [h.date, h]));

    // Calculate Fair Market Value deltas using super_bowl_odds from price history
    const fmvDeltas = history
        .filter(entry => entry.super_bowl_odds !== null && entry.super_bowl_odds !== undefined)
        .map(entry => {
            const odds = entry.super_bowl_odds;
            let impliedProb;
            
            // Calculate implied probability from American odds
            if (odds > 0) {
                impliedProb = 100 / (odds + 100);
            } else {
                impliedProb = (-odds) / (-odds + 100);
            }
            
            // Amount needed to bet to make $1000
            const betAmount = 1000 * impliedProb;
            
            // FMV = $FMV + bet amount
            const currentFMV = betAmount;
            
            return {
                date: entry.date,
                currentFMV: currentFMV,
                odds: odds
            };
        })
        .reverse();

    // Calculate FMV deltas (compare consecutive entries)
    const fmvDeltasWithChange = fmvDeltas.map((entry, idx) => {
        const prevEntry = fmvDeltas[idx + 1];
        const delta = prevEntry ? entry.currentFMV - prevEntry.currentFMV : 0;
        
        return {
            ...entry,
            prevFMV: prevEntry ? prevEntry.currentFMV : entry.currentFMV,
            delta: delta
        };
    });

    const yoyDeltas = history
        .map(entry => {
            const currentDate = new Date(entry.date);
            const prevYearDate = new Date(currentDate);
            prevYearDate.setFullYear(prevYearDate.getFullYear() - 1);

            const prevDateStr = prevYearDate.toISOString().split('T')[0];
            const prevEntry = historyMap[prevDateStr];

            if (prevEntry) {
                const delta = entry.price - prevEntry.price;
                return {
                    date: entry.date,
                    currentPrice: entry.price,
                    prevPrice: prevEntry.price,
                    delta
                };
            }
            return null;
        })
        .filter(entry => entry !== null)
        .reverse();

    // If not authenticated, show a message or redirect
    if (!user) {
        return (
            <div className="password-container">
                <h2>
                    Please <Link to="/login">log in</Link> to view team details.
                </h2>
            </div>
        );
    }

    return (
        <div className="team-detail-container">
            <div className="team-detail-card">
                <Link to="/exchange" className="btn btn-secondary mb-3">&larr; Back to Exchange</Link>
                {todayPrice && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 0,
                            marginBottom: 12,
                            color: "var(--text-primary)"
                        }}
                    >
                        <h4 className="current-price-header" style={{ margin: 0, color: "var(--text-primary)" }}>
                            {team_symbol}:{" "}
                            <span
                                className={highlight ? "highlight" : ""}
                                style={{ color: "#007bff" }}
                            >
                                ${todayPrice.price.toFixed(2)}
                                {todayPrice.price === 1000 && <span style={{ marginLeft: '8px', fontSize: '1.2em', color: "#FFD700" }}>👑 CHAMPION</span>}
                            </span>
                        </h4>
                        <h4 className="current-sport-header" style={{ margin: 0, color: "var(--text-primary)" }}>
                            {teamSport}
                        </h4>
                    </div>
                )}
                <h2 className="team-title">{teamName} Price History</h2>

                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden"></span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="chart-section">
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart data={history} margin={{ top: 5, right: 0, bottom: 5, left: -5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" tickFormatter={formatDateLabel} tick={{ fontSize: 12 }} minTickGap={20} />
                                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} tickFormatter={(value) => value.toFixed(2)} />
                                    <Tooltip />
                                    <Line 
                                        type="monotone" 
                                        dataKey="price" 
                                        stroke={isDarkMode ? "rgb(58, 183, 187)" : "#2f00ff7e"} 
                                        strokeWidth={2} 
                                        dot={false} 
                                        isAnimationActive={false} 
                                        name="Price"
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="fmv" 
                                        stroke={isDarkMode ? "rgb(197, 202, 126)" : "#ff3300ff"} 
                                        strokeWidth={2} 
                                        strokeDasharray="5 5"
                                        dot={false} 
                                        isAnimationActive={false} 
                                        name="Fair Market Value"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Order Form */}
                        <div className="buy-sell-row">
                            <div className="buy-sell-col">
                                <label>Order Type</label>
                                <select className="form-control mb-2" value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                                    <option value="buy">Buy</option>
                                    <option value="sell">Sell</option>
                                </select>

                                <label>Price</label>
                                <div className="price-input-row">
                                    <input
                                        type="number"
                                        className="form-control mb-2 price-input-90"
                                        value={price}
                                        step="0.01"
                                        onChange={e => setPrice(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary price-refresh-btn"
                                        title="Reset to current price"
                                        onClick={() => todayPrice && setPrice(todayPrice.price)}
                                    >
                                        &#x21bb;
                                    </button>
                                </div>
                                {/* <div style={{fontSize: "0.9em", color: "#888", marginBottom: 8}}>
                                    Allowed range: ${minPrice} – ${maxPrice}
                                </div> */}
                                <div className="quick-quantity-options">
                                    {[-1, 'FMV', 1].map((perc) => (
                                        <button
                                            key={perc}
                                            type="button"
                                            className="quick-quantity-btn"
                                            onClick={() => {
                                                if (perc === 'FMV') {
                                                    // Set price to Fair Market Value
                                                    if (teamStats.current_odds !== null && teamStats.current_odds !== undefined && priceStats.class_b_price !== null && priceStats.class_b_price !== undefined) {
                                                        const odds = teamStats.current_odds;
                                                        let impliedProb;
                                                        
                                                        // Calculate implied probability from American odds
                                                        if (odds > 0) {
                                                            impliedProb = 100 / (odds + 100);
                                                        } else {
                                                            impliedProb = (-odds) / (-odds + 100);
                                                        }
                                                        
                                                        // Amount needed to bet to make $1000
                                                        const betAmount = 1000 * impliedProb;
                                                        
                                                        // FMV = class_b_price + bet amount
                                                        const fmv = parseFloat(priceStats.class_b_price || 0) + betAmount;

                                                        setPrice(fmv.toFixed(2));
                                                    }
                                                } else {
                                                    // Original percentage logic for -1 and 1
                                                    setPrice(prev => {
                                                        const current = Number(prev) || 0;
                                                        return (current + current * (perc / 100)).toFixed(2);
                                                    });
                                                }
                                            }}
                                        >
                                            {perc === 'FMV' ? 'FMV' : `${perc > 0 ? "+" : ""}${perc}% price`}
                                        </button>
                                    ))}
                                </div>
                                <label>Quantity</label>
                                <input
                                    type="number"
                                    className="form-control mb-2"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    placeholder="Enter quantity"
                                />
                                <div className="quick-quantity-options">
                                    {[1, 5, 10].map((qty) => (
                                        <button
                                            key={qty}
                                            type="button"
                                            className="quick-quantity-btn"
                                                onClick={() => setQuantity(prev => {
                                                const current = Number(prev) || 0;
                                                return current + qty;
                                            })}
                                        >
                                            +{qty} share{qty > 1 ? "s" : ""}
                                        </button>
                                    ))}
                                </div>
                                <div>
                                    <small style={{ color: "var(--text-secondary)" }}>
                                    All orders are good til' cancelled
                                    </small>
                                </div>
                                <div className="order-total-box" style={{
                                    background: isDarkMode ? '#1f1f1f' : '#f9f9f9',
                                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                                    borderRadius: '6px',
                                    padding: '12px',
                                    marginBottom: '12px',
                                    textAlign: 'center'
                                    }}>
                                    <div style={{ fontSize: '0.9em', color: isDarkMode ? '#aaa' : '#666', marginBottom: '6px' }}>
                                        Total Order Amount
                                    </div>
                                    <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#007bff' }}>
                                        ${(Number(price) * Number(quantity) || 0).toFixed(2)}
                                    </div>
                                    </div>
                                <button
                                    className="btn btn-primary"
                                    style={{ maxHeight: '50px' }}
                                    onClick={async () => {
                                        if (isSubmitting) return;  // Prevent duplicate submissions

                                        // const numPrice = Number(price);
                                        // if (numPrice < minPrice || numPrice > maxPrice) {
                                        //     setOrderMessage(`Price must be between $${minPrice} and $${maxPrice}`);
                                        //     return;
                                        // }
                                        
                                        // Validate quantity
                                        // Validate quantity

                                        const numQuantity = Number(quantity);
                                        if (!quantity || numQuantity <= 0) {
                                            setOrderMessage("Quantity must be greater than 0");
                                            return;
                                        }
                                        
                                        // Validate price
                                        const numPrice = Number(price);
                                        if (!price || numPrice <= 0) {
                                            setOrderMessage("Price must be greater than 0");
                                            return;
                                        }
                                        
                                        setIsSubmitting(true);
                                        try {
                                            // Add minimum delay for UX
                                            const [response] = await Promise.all([
                                                fetch(`${API_BASE_URL}/api/exchange/order`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                        user_id: user.id,
                                                        security_symbol: team_symbol,
                                                        type: orderType,
                                                        price: Number(price),
                                                        quantity: Number(quantity)
                                                    })
                                                }),
                                                new Promise(resolve => setTimeout(resolve, 500)) // 0.5s minimum delay
                                            ]);
                                            
                                            const result = await response.json();
                                            if (!result.success) {
                                                throw new Error(result.error || 'Failed to place order');
                                            }
                                            
                                            setOrderMessage("Order placed successfully!");
                                            setPrice("");
                                            setQuantity("");

                                            // 🔁 Refresh data
                                            await Promise.all([
                                                fetchTrades(),
                                                fetchUserProfile()
                                            ]);
                                            window.location.reload();
                                        } catch (err) {
                                            console.error(err);

                                            let cleanMessage = "There was an issue placing your order.";

                                            if (err.message.includes("Insufficient funds")) {
                                                cleanMessage = "You don't have enough balance to place this buy order.";
                                            } else if (err.message.includes("Insufficient shares")) {
                                                cleanMessage = "You don't own enough of this security to sell that quantity.";
                                            } else if (err.message.includes("Security not found")) {
                                                cleanMessage = "This team/security is no longer available.";
                                            }

                                            setOrderMessage(cleanMessage);
                                        } finally {
                                            setIsSubmitting(false);
                                        }
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Submit Order
                                </button>
                                
                                {orderMessage && <div className="mt-2 text-info">{orderMessage}</div>}
                                {isSubmitting && (
                                    <div className="modal-overlay">
                                        <div className="modal-content" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                                            <h3>Submitting Trade...</h3>
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden"></span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Recent Trades */}
                            <div className="table-section">
                                <div className="stats-24hr-box">
                                    <strong>24h High:</strong> {stats24hr.high && typeof stats24hr.high === "object" && stats24hr.high.highPrice !== undefined
                                        ? `$${Number(stats24hr.high.highPrice).toFixed(2)}`
                                        : "--"} &nbsp;|&nbsp;
                                    <strong>24h Low:</strong> {stats24hr.low && typeof stats24hr.low === "object" && stats24hr.low.lowPrice !== undefined
                                        ? `$${Number(stats24hr.low.lowPrice).toFixed(2)}`
                                        : "--"} &nbsp;|&nbsp;
                                    <strong>24h Volume:</strong> {stats24hr.volume && typeof stats24hr.volume === "object" && stats24hr.volume.volume !== undefined
                                        ? stats24hr.volume.volume
                                        : "--"}
                                </div>
                                <div className="stats-24hr-box">
                                    <strong>IPO {sport_champ} Odds:</strong> {teamStats.super_bowl_odds !== null && teamStats.super_bowl_odds !== undefined
                                        ? `${teamStats.super_bowl_odds > 0 ? '+' : ''}${teamStats.super_bowl_odds.toLocaleString()}`
                                        : "--"} &nbsp;|&nbsp;
                                    {/* <strong>Conf Odds:</strong> {teamStats.conf_odds !== null && teamStats.conf_odds !== undefined
                                        ? `${teamStats.conf_odds > 0 ? '+' : ''}${teamStats.conf_odds}`
                                        : "--"} &nbsp;|&nbsp; */}
                                    <strong>Current {sport_champ} Odds:</strong> {teamStats.current_odds !== null && teamStats.current_odds !== undefined
                                        ? `${teamStats.current_odds > 0 ? '+' : ''}${teamStats.current_odds.toLocaleString()} (${teamStats.odds_provider || 'N/A'})`  
                                        : "--"}  &nbsp;
                                        {/* |&nbsp; */}

                                </div>
                                <div className="stats-24hr-box">
                                    <strong>Fair Market Value:</strong> {teamStats.current_odds !== "OFF" && teamStats.current_odds !== null && teamStats.current_odds !== undefined && priceStats.class_b_price !== null && priceStats.class_b_price !== undefined
                                        ? (() => {
                                            const odds = teamStats.current_odds;
                                            let impliedProb;
                                            
                                            // Calculate implied probability from American odds
                                            if (odds > 0) {
                                                // Positive odds: implied probability = 100 / (odds + 100)
                                                impliedProb = 100 / (odds + 100);
                                            } else {
                                                // Negative odds: implied probability = (-odds) / (-odds + 100)
                                                impliedProb = (-odds) / (-odds + 100);
                                            }
                                            
                                            // Amount needed to bet to make $1000
                                            const betAmount = 1000 * impliedProb;
                                            
                                            // FMV = class_b_price + bet amount
                                            const fmv = parseFloat(priceStats.class_b_price || 0) + betAmount;
                                            
                                            return `$${fmv.toFixed(2)}`;
                                        })()
                                        : "--"}
                                    
                                    {/* <strong>Class A:</strong> {priceStats.class_a_price !== null && priceStats.class_a_price !== undefined
                                        ? `$${Number(priceStats.class_a_price).toFixed(2)}`
                                        : "--"} &nbsp;|&nbsp; */}
                                    {/* <strong>Class B:</strong> {priceStats.class_b_price !== null && priceStats.class_b_price !== undefined
                                        ? `$${Number(priceStats.class_b_price).toFixed(2)}`
                                        : "--"} (next bet: {priceStats.next_bet !== null && priceStats.next_bet !== undefined
                                        ? `$${Number(priceStats.next_bet).toFixed(2)}`
                                        : "--"})* */}
                                </div>
                                <h5 className="history-table-title">Recent Trades</h5>
                                <table className="table table-bordered team-history-table">
                                    <thead>
                                        <tr>
                                            <th>Time</th>
                                            <th>Price</th>
                                            <th>Qty</th>
                                            <th>By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentTrades.map((trade, idx) => (
                                            // console.log(trade),
                                            <tr key={idx}>
                                                <td>{new Date(trade.timestamp).toLocaleString()}</td>
                                                <td>${trade.price}</td>
                                                <td>{trade.quantity}</td>
                                                <td>{trade.buyer_id ? trade.buyer_id.slice(-4) : '----'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Order Book */}
                        <div className="table-section-container">
                        <div className="table-section">
                            <h5 className="history-table-title">Top Bids</h5>
                            <table className="table table-bordered team-history-table">
                            <thead><tr><th>Price</th><th>Qty</th></tr></thead>
                            <tbody>
                                {orderBook.bids.map((bid, idx) => (
                                <tr key={idx}><td>${bid.price.toFixed(2)}</td><td>{bid.quantity - bid.filled_quantity}</td></tr>
                                ))}
                            </tbody>
                            </table>
                        </div>

                        <div className="table-section">
                            <h5 className="history-table-title">Top Asks</h5>
                            <table className="table table-bordered team-history-table">
                            <thead><tr><th>Price</th><th>Qty</th><th>By</th></tr></thead>
                            <tbody>
                                {orderBook.asks.map((ask, idx) => (
                                <tr key={idx}><td>${ask.price.toFixed(2)}</td><td>{ask.quantity - ask.filled_quantity}</td><td>{ask.user_id ? ask.user_id.slice(-4) === "b589" ? 'IPO' : ask.user_id.slice(-4) : '----'}</td></tr>
                                ))}
                            </tbody>
                            </table>
                        </div>
                        </div>
                        {/* Weekly and Yearly Price Tables */}
                        <div className="table-section-container">
                            <div className="table-section">
                                <h5 className="history-table-title">Historical Price</h5>
                                <table className="table table-bordered team-history-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Price</th>
                                            <th>Change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reversedHistory.map((entry, idx) => {
                                            const prev = reversedHistory[idx + 1]?.price;
                                            const diff = prev !== undefined ? entry.price - prev : null;
                                            let diffDisplay = null;
                                            if (diff !== null && diff !== 0) {
                                                diffDisplay = (
                                                    <span style={{ color: diff > 0 ? 'green' : 'red', marginLeft: 6, fontWeight: 500 }}>
                                                        {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                                                    </span>
                                                );
                                            } else if (diff === 0) {
                                                diffDisplay = <span style={{ color: '#888', marginLeft: 6 }}>0.00</span>;
                                            }
                                            return (
                                                <tr key={idx}>
                                                    <td>{entry.date}</td>
                                                    <td>${entry.price.toFixed(2)}</td>
                                                    <td>
                                                        {getArrow(entry.price, prev)}
                                                        {diffDisplay}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="table-section">
                                <h5 className="history-table-title">Historical Fair Market Value</h5>
                                <table className="table table-bordered team-history-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Odds</th>
                                            <th>Fair Market Value</th>
                                            <th>Change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fmvDeltasWithChange.map((entry, idx) => (
                                            <tr key={idx}>
                                            <td>{entry.date}</td>
                                            <td>{entry.odds !== null && entry.odds !== undefined
                                                ? `${entry.odds > 0 ? '+' : ''}${entry.odds.toLocaleString()}`
                                                : '--'}</td>
                                            <td>${entry.currentFMV.toFixed(2)}</td>
                                            <td>
                                                {getArrow(entry.currentFMV, entry.prevFMV)}
                                                <span style={{ color: entry.delta > 0 ? 'green' : entry.delta < 0 ? 'red' : '#888', marginLeft: 6, fontWeight: 500 }}>
                                                    {entry.delta > 0 ? '+' : ''}
                                                    {entry.delta.toFixed(2)}
                                                </span>
                                            </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            <div className="stats-24hr-box">
                <strong>*Each Share pays $1,000 for {sport_champ} win.</strong> 
                {/* <strong>Class B shares distribute dividends for every team win</strong> */}
            </div>
            </div>
        </div>
    );
}

export default TeamDetail;
